/**
 * Interface language and presentation band.
 *
 * Strings live next to the code that uses them as `L(english, norsk)` pairs, so
 * a translation can never drift away from the text it translates. The active
 * language is module state: App sets it before rendering and remounts the tree
 * when it changes, so every `L()` call sees the current language.
 */

export type Lang = 'nb' | 'en';
export const LANGS: { id: Lang; label: string; short: string }[] = [
  { id: 'nb', label: 'Norsk (bokmål)', short: 'NB' },
  { id: 'en', label: 'English', short: 'EN' },
];

/** Presentation band derived from the learner's stage (or chosen in Settings). */
export type Band = 'junior' | 'middle' | 'senior';

let lang: Lang = detectLang();
let band: Band = 'senior';

export function detectLang(): Lang {
  if (typeof navigator === 'undefined') return 'en';
  const langs = [...(navigator.languages ?? []), navigator.language].filter(Boolean).map((l) => l.toLowerCase());
  return langs.some((l) => l.startsWith('nb') || l.startsWith('no') || l.startsWith('nn')) ? 'nb' : 'en';
}

export const getLang = () => lang;
export const getBand = () => band;

export function setLang(next: Lang) {
  lang = next;
  if (typeof document !== 'undefined') document.documentElement.lang = next === 'nb' ? 'nb' : 'en';
}

export function setBand(next: Band) {
  band = next;
}

/** Pick the string for the active language. */
export function L(en: string, nb: string): string {
  return lang === 'nb' ? nb : en;
}

/**
 * Language difficulty of the interface (Settings → Language difficulty).
 *  - easy: shortest, plainest words (default for grade 1–4)
 *  - normal: everyday words with a game feel (default)
 *  - detailed: the precise, more technical wording
 */
export type TextLevel = 'easy' | 'normal' | 'detailed';
let textLevel: TextLevel = 'normal';
export const getTextLevel = () => textLevel;
export function setTextLevel(next: TextLevel) {
  textLevel = next;
}

type Pair = readonly [en: string, nb: string];

/**
 * Text at three difficulty levels. `en`/`nb` is the normal wording; `easy` and
 * `detailed` are optional alternatives (missing ones fall back to normal).
 */
export function LL(en: string, nb: string, alt: { easy?: Pair; detailed?: Pair }): string {
  const pick = (textLevel === 'easy' && alt.easy) || (textLevel === 'detailed' && alt.detailed) || [en, nb];
  return lang === 'nb' ? pick[1] : pick[0];
}

/** Like L, with a simpler variant for the easy language level. */
export function LJ(en: string, nb: string, easyEn: string, easyNb: string): string {
  if (textLevel === 'easy') return lang === 'nb' ? easyNb : easyEn;
  return lang === 'nb' ? nb : en;
}

/** Bilingual value used by content definitions. */
export type Bi = string | { en: string; nb: string };
export function bi(v: Bi | undefined): string {
  if (v === undefined) return '';
  return typeof v === 'string' ? v : v[lang] ?? v.en;
}
export const B = (en: string, nb: string): { en: string; nb: string } => ({ en, nb });

const isBi = (v: unknown): v is { en: string; nb: string } =>
  !!v && typeof v === 'object' && !Array.isArray(v) && typeof (v as { en?: unknown }).en === 'string' && typeof (v as { nb?: unknown }).nb === 'string' && Object.keys(v as object).length === 2;

type Localized<T> = { [K in keyof T]: T[K] extends { en: string; nb: string } ? string : T[K] };

/**
 * Turns `{ title: B('Seeker', 'Søker') }` into an object whose `title` is a getter
 * returning the active language — consumers keep reading plain strings.
 */
export function loc<T extends object>(def: T): Localized<T> {
  const out = {} as Record<string, unknown>;
  for (const [k, v] of Object.entries(def)) {
    if (isBi(v)) Object.defineProperty(out, k, { get: () => bi(v), enumerable: true });
    else out[k] = v;
  }
  return out as Localized<T>;
}

/** A structure where any string may instead be a bilingual `B(en, nb)` pair. */
export type DeepBi<T> = T extends string
  ? T | { en: string; nb: string }
  : T extends (infer U)[]
    ? DeepBi<U>[]
    : T extends object
      ? { [K in keyof T]: DeepBi<T[K]> }
      : T;

/** Resolve every bilingual pair inside a structure to the active language. */
export function resolveBi<T>(v: DeepBi<T>): T {
  if (isBi(v)) return bi(v) as T;
  if (Array.isArray(v)) return v.map((x) => resolveBi(x)) as T;
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) out[k] = resolveBi(x as DeepBi<unknown>);
    return out as T;
  }
  return v as T;
}

/** Locale-aware number formatting with Norwegian spacing/decimal comma. */
export function num(n: number, maxFrac = 2): string {
  return n.toLocaleString(lang === 'nb' ? 'nb-NO' : 'en-GB', { maximumFractionDigits: maxFrac });
}

export function plural(n: number, one: [string, string], many: [string, string]): string {
  const [en, nb] = n === 1 ? one : many;
  return `${n} ${L(en, nb)}`;
}

export function locale(): string {
  return lang === 'nb' ? 'nb-NO' : 'en-GB';
}
